import * as stylex from '@stylexjs/stylex'
import Avatar from "../../components/Avatar";
import Icon from "../../components/Icon";
import {
  findGroup,
  findPerson,
  formatActivityDate,
  formatMoney,
  groupSpent,
  type Balance,
  type Overview,
} from "../../lib/ledger";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";


const styles = stylex.create({
  s0: {
    fontSize: "12px",
    fontWeight: 500,
    opacity: 0.75,
  },
  s1: {
    marginTop: "8px",
    fontSize: "25px",
    fontWeight: 600,
    letterSpacing: "-.035em",
  },
  s2: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#6d776f",
  },
  s3: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "#fff",
    padding: "16px",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
    "@media (min-width: 640px)": {
      padding: "20px",
    },
  },
  s4: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
  },
  s5: {
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "-.015em",
  },
  s6: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#748077",
  },
  s7: {
    textAlign: "right",
  },
  s8: {
    fontSize: "11px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    color: "#889188",
  },
  s9: {
    marginTop: "20px",
    height: "224px",
    width: "100%",
  },
  s10: {
    marginTop: "20px",
    display: "flex",
    height: "224px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    backgroundColor: "#f7faf6",
    textAlign: "center",
  },
  s11: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#4d6251",
  },
  s12: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#879087",
  },
  s13: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  s14: {
    display: "flex",
    height: "36px",
    width: "36px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    backgroundColor: "#f1f6ef",
    color: "#4a8258",
  },
  s15: {
    height: "18px",
    width: "18px",
  },
  s16: {
    marginTop: "16px",
    borderTopWidth: 1,
    borderTopColor: "#edf0eb",
  },
  s17: {
    display: "grid",
    width: "100%",
    gridTemplateColumns: "minmax(0,1fr) auto",
    gap: "12px",
    borderRadius: "0.5rem",
    paddingBlock: "12px",
    cursor: "pointer",
    textAlign: "left",
    transition: "background-color 160ms ease",
    ":hover": {
      backgroundColor: "#f7faf6",
    },
    ":first-child": {
      paddingTop: "0px",
    },
    ":last-child": {
      paddingBottom: "0px",
    },
  },
  s18: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    gap: "12px",
  },
  s19: {
    minWidth: "0px",
  },
  s20: {
    display: "block",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 500,
    color: "#2d352f",
  },
  s21: {
    marginTop: "2px",
    display: "block",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: "#758077",
  },
  s22: {
    marginTop: "2px",
    display: "block",
    fontSize: "11px",
    color: "#879087",
  },
  s23: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
    "@media (min-width: 640px)": {
      display: "flex",
      flexDirection: "column",
      rowGap: "28px",
    },
  },
  s24: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      alignItems: "end",
    },
  },
  s25: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#5b9164",
  },
  s26: {
    marginTop: "4px",
    fontSize: "clamp(1.75rem,3.2vw,2.25rem)",
    fontWeight: 600,
    letterSpacing: "-.045em",
    color: "#202720",
  },
  s27: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#707b72",
  },
  s28: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  s29: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#c9dac8",
    backgroundColor: "#fff",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#476d50",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f4f8f2",
    },
  },
  s30: {
    height: "16px",
    width: "16px",
  },
  s31: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c49",
    },
  },
  s32: {
    display: "grid",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "minmax(0,1.4fr) repeat(2,minmax(0,1fr))",
    },
  },
  s33: {
    overflow: "hidden",
    borderRadius: "1rem",
    backgroundImage: "linear-gradient(135deg,#3f8551,#6aa073)",
    padding: "20px",
    color: "#fff",
    boxShadow: "0 10px 25px rgba(47,112,64,.18)",
    "@media (min-width: 640px)": {
      padding: "24px",
    },
  },
  s34: {
    fontSize: "13px",
    fontWeight: 500,
    color: "color-mix(in srgb, #fff 75%, transparent)",
  },
  s35: {
    marginTop: "8px",
    fontSize: "clamp(2rem,4vw,2.7rem)",
    fontWeight: 600,
    letterSpacing: "-.055em",
  },
  s36: {
    marginTop: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "color-mix(in srgb, #fff 85%, transparent)",
  },
  s37: {
    display: "flex",
    height: "20px",
    width: "20px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    backgroundColor: "color-mix(in srgb, #fff 15%, transparent)",
  },
  s38: {
    height: "14px",
    width: "14px",
  },
  s39: {
    display: "grid",
    alignItems: "start",
    gap: "20px",
    "@media (min-width: 1280px)": {
      gridTemplateColumns: "minmax(0,1.35fr) minmax(330px,.85fr)",
    },
  },
  s40: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  s41: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#3c7d4d",
    ":hover": {
      textDecoration: "underline",
    },
  },
  s42: {
    marginTop: "16px",
    display: "grid",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s43: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "#fdfefd",
    padding: "16px",
    textAlign: "left",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      borderColor: "#bdd5bf",
      boxShadow: "0 8px 18px rgba(47,92,54,.07)",
    },
  },
  s44: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "12px",
  },
  s45: {
    height: "20px",
    width: "20px",
  },
  s46: {
    height: "16px",
    width: "16px",
    color: "#91a096",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      transform: "translateX(0px)",
      color: "#3c7b4d",
    },
  },
  s47: {
    marginTop: "16px",
    fontSize: "14px",
    fontWeight: 600,
  },
  s48: {
    marginTop: "4px",
    minHeight: "32px",
    fontSize: "11px",
    lineHeight: "1rem",
    color: "#778179",
  },
  s49: {
    marginTop: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderColor: "#edf0ec",
    paddingTop: "12px",
    fontSize: "11px",
  },
  s50: {
    color: "#728076",
  },
  s51: {
    fontWeight: 600,
    color: "#48694f",
  },
  s52: {
    marginTop: "16px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  s53: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#c8d8c6",
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgb(38 59 42 / 7%)",
    padding: "12px",
  },
  s54: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: "12px",
    borderRadius: "0.5rem",
    textAlign: "left",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f3f8f1",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#5a8f66",
    },
  },
  s55: {
    minWidth: "0px",
    flex: "1 1 0%",
  },
  s56: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 600,
  },
  s57: {
    marginTop: "12px",
    height: "36px",
    width: "100%",
    borderRadius: "0.5rem",
    backgroundColor: "#f2f6f0",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397849",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#e8f1e5",
    },
  },
  s58: {
    marginTop: "20px",
    borderRadius: "0.75rem",
    backgroundColor: "#f5faf3",
    paddingInline: "16px",
    paddingBlock: "32px",
    textAlign: "center",
  },
  s59: {
    marginInline: "auto",
    display: "flex",
    height: "40px",
    width: "40px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    backgroundColor: "#e6f2e3",
    color: "#4b8a58",
  },
  s60: {
    height: "20px",
    width: "20px",
  },
  s61: {
    marginTop: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  s62: {
    marginTop: "16px",
    display: "flex",
    height: "40px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dce6da",
    fontSize: "12px",
    fontWeight: 600,
    color: "#45684d",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f5f9f4",
    },
  },
})

const dashboardStyles = stylex.create({
  summary: { borderRadius: '1rem', borderWidth: 1, padding: '16px', '@media (min-width: 640px)': { padding: '20px' } },
  summaryGreen: { borderColor: '#d8e9d7', backgroundColor: '#f5fbf3', color: '#287541' },
  summaryOrange: { borderColor: '#f0dcca', backgroundColor: '#fff9f2', color: '#a95420' },
  summaryNeutral: { borderColor: '#e0e6de', backgroundColor: '#fff', color: '#263128' },
  netPositive: { color: '#2f7c45' },
  netNegative: { color: '#a34d1b' },
  activityIcon: { display: 'flex', height: '36px', width: '36px', flexShrink: 0, alignItems: 'center', justifyContent: 'center', borderRadius: '0.75rem' },
  settlementIcon: { backgroundColor: '#f3f0e8', color: '#997248' },
  expenseIcon: { backgroundColor: '#eef6eb', color: '#478555' },
  activityAmount: { display: 'block', fontSize: '13px', fontWeight: 600 },
  positive: { color: '#2f7c45' },
  negative: { color: '#a34d1b' },
  groupIcon: { display: 'flex', height: '40px', width: '40px', alignItems: 'center', justifyContent: 'center', borderRadius: '0.75rem' },
  groupIconGreen: { backgroundColor: '#e9f4e6', color: '#427f51' },
  groupIconTan: { backgroundColor: '#f6efe4', color: '#95683f' },
  settlementLabel: { marginTop: '2px', fontSize: '11px' },
  receivedLabel: { color: '#397a4b' },
  owedLabel: { color: '#a4501d' },
  settlementAmount: { fontSize: '13px', fontWeight: 600 },
  receivedAmount: { color: '#2e7943' },
  owedAmount: { color: '#a24e1c' },
})
type DashboardPageProps = {
  data: Overview;
  onAddExpense: (groupID?: string) => void;
  onCreateGroup: () => void;
  onOpenExpense: (expenseID: string) => void;
  onOpenGroup: (groupID: string) => void;
  onOpenPerson: (personID: string) => void;
  onSettle: (balance: Balance) => void;
};

type ActivityItem = {
  amount: number;
  date: string;
  detail: string;
  id: string;
  kind: "expense" | "settlement";
  personID?: string;
  positive: boolean;
  title: string;
};

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function SummaryCard({
  amount,
  caption,
  label,
  tone = "green",
}: {
  amount: number;
  caption: string;
  label: string;
  tone?: "green" | "orange" | "neutral";
}) {
  return (
    <article {...stylex.props(dashboardStyles.summary, tone === 'green' ? dashboardStyles.summaryGreen : tone === 'orange' ? dashboardStyles.summaryOrange : dashboardStyles.summaryNeutral)}>
      <p {...stylex.props(styles.s0)}>{label}</p>
      <p {...stylex.props(styles.s1)}>
        {caption}
      </p>
      <p {...stylex.props(styles.s2)}>
        {amount === 0
          ? "You are all settled up"
          : amount > 0
            ? "Money coming back to you"
            : "Money you can settle now"}
      </p>
    </article>
  );
}

type BalancePoint = { date: string; value: number };

function userShare(expense: Overview["expenses"][number], userID: string) {
  const participantIndex = expense.participantIds.indexOf(userID);
  if (participantIndex < 0 || expense.participantIds.length === 0) return 0;
  const baseShare = Math.floor(
    expense.amountCents / expense.participantIds.length,
  );
  return (
    baseShare +
    (participantIndex < expense.amountCents % expense.participantIds.length
      ? 1
      : 0)
  );
}

function NetBalanceChart({ data }: { data: Overview }) {
  const currency = data.currentUser.currency;
  const events = [
    ...data.expenses
      .filter((expense) => expense.currency === currency)
      .map((expense) => {
        const share = userShare(expense, data.currentUser.id);
        return {
          date: expense.occurredAt ?? expense.createdAt,
          amount:
            expense.paidById === data.currentUser.id
              ? expense.amountCents - share
              : -share,
        };
      }),
    ...data.settlements
      .filter((settlement) => settlement.currency === currency)
      .map((settlement) => ({
        date: settlement.paidAt ?? settlement.createdAt,
        amount:
          settlement.direction === "received"
            ? -settlement.amountCents
            : settlement.amountCents,
      })),
  ].sort(
    (left, right) =>
      new Date(left.date).getTime() - new Date(right.date).getTime(),
  );

  const points: BalancePoint[] = events.reduce<BalancePoint[]>(
    (history, event) => {
      const previous = history.at(-1)?.value ?? 0;
      return [...history, { date: event.date, value: previous + event.amount }];
    },
    [],
  );
  const currentNet = data.balances
    .filter((balance) => balance.currency === currency)
    .reduce((sum, balance) => sum + balance.amountCents, 0);

  return (
    <section {...stylex.props(styles.s3)}>
      <div {...stylex.props(styles.s4)}>
        <div>
          <h2 {...stylex.props(styles.s5)}>
            Net balance over time
          </h2>
          <p {...stylex.props(styles.s6)}>
            How much others owe you, after shared expenses and payments.
          </p>
        </div>
        <div {...stylex.props(styles.s7)}>
          <p {...stylex.props(styles.s8)}>
            Current
          </p>
          <p
            {...stylex.props(styles.s1, currentNet >= 0 ? dashboardStyles.netPositive : dashboardStyles.netNegative)}
          >
            {formatMoney(Math.abs(currentNet), currency)}
          </p>
        </div>
      </div>
      {points.length > 0 ? (
        <div {...stylex.props(styles.s9)}>
          <ResponsiveContainer height="100%" width="100%">
            <LineChart
              data={points}
              margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
            >
              <CartesianGrid
                stroke="#dce9da"
                strokeDasharray="3 3"
                vertical={false}
              />
              <ReferenceLine stroke="#b7c6b7" strokeDasharray="4 4" y={0} />
              <XAxis
                axisLine={false}
                dataKey="date"
                minTickGap={32}
                tick={{ fill: "#7b887d", fontSize: 10 }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                }
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tick={{ fill: "#7b887d", fontSize: 10 }}
                tickFormatter={(value) => formatMoney(Number(value), currency)}
                tickLine={false}
                width={84}
              />
              <Tooltip
                contentStyle={{
                  background: "#fffefa",
                  border: "1px solid #d9e6d7",
                  borderRadius: 10,
                  boxShadow: "0 6px 18px rgba(48,70,51,.12)",
                  fontSize: 12,
                }}
                formatter={(value) => formatMoney(Number(value), currency)}
                labelFormatter={(value) =>
                  new Date(String(value)).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                }
              />
              <Line
                activeDot={{ r: 4, strokeWidth: 2 }}
                dataKey="value"
                dot={false}
                isAnimationActive={false}
                name="Net balance"
                stroke="#438b57"
                strokeWidth={2.5}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div {...stylex.props(styles.s10)}>
          <div>
            <p {...stylex.props(styles.s11)}>
              No balance history yet
            </p>
            <p {...stylex.props(styles.s6)}>
              Add a shared expense to start tracking your net balance.
            </p>
          </div>
        </div>
      )}
      <p {...stylex.props(styles.s12)}>
        Positive values mean money is coming back to you. Values are shown in{" "}
        {currency}.
      </p>
    </section>
  );
}

function RecentActivity({
  data,
  onOpenExpense,
  onOpenPerson,
}: Pick<DashboardPageProps, "data" | "onOpenExpense" | "onOpenPerson">) {
  const activities: ActivityItem[] = [
    ...data.expenses.map((expense) => {
      const payer = findPerson(data, expense.paidById);
      const group = findGroup(data, expense.groupId);
      const paidByYou = expense.paidById === data.currentUser.id;
      return {
        id: expense.id,
        kind: "expense" as const,
        date: expense.occurredAt ?? expense.createdAt,
        title: expense.description,
        detail: `${paidByYou ? "You paid" : `${payer?.name ?? "Someone"} paid`} · ${group?.name ?? "Shared expense"}`,
        amount: expense.amountCents,
        positive: paidByYou,
      };
    }),
    ...data.settlements.map((settlement) => {
      const person = findPerson(data, settlement.personId);
      const received = settlement.direction === "received";
      return {
        id: settlement.id,
        kind: "settlement" as const,
        date: settlement.paidAt ?? settlement.createdAt,
        title: received ? "Payment received" : "Payment sent",
        detail: received
          ? `${person?.name ?? "Someone"} paid you`
          : `You paid ${person?.name ?? "someone"}`,
        amount: settlement.amountCents,
        personID: settlement.personId,
        positive: received,
      };
    }),
  ]
    .sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    )
    .slice(0, 5);

  return (
    <section {...stylex.props(styles.s3)}>
      <div {...stylex.props(styles.s13)}>
        <div>
          <h2 {...stylex.props(styles.s5)}>
            Recent activity
          </h2>
          <p {...stylex.props(styles.s6)}>
            What changed most recently
          </p>
        </div>
        <span {...stylex.props(styles.s14)}>
          <Icon {...stylex.props(styles.s15)} name="activity" />
        </span>
      </div>
      <div {...stylex.props(styles.s16)}>
        {activities.map((item) => (
          <button
            {...stylex.props(styles.s17)}
            key={item.id}
            onClick={() => item.kind === "expense" ? onOpenExpense(item.id) : item.personID && onOpenPerson(item.personID)}
            type="button"
          >
            <span {...stylex.props(styles.s18)}>
              <span
                {...stylex.props(dashboardStyles.activityIcon, item.kind === "settlement" ? dashboardStyles.settlementIcon : dashboardStyles.expenseIcon)}
              >
                <Icon
                  {...stylex.props(styles.s15)}
                  name={item.kind === "settlement" ? "settle" : "receipt"}
                />
              </span>
              <span {...stylex.props(styles.s19)}>
                <span {...stylex.props(styles.s20)}>
                  {item.title}
                </span>
                <span {...stylex.props(styles.s21)}>
                  {item.detail}
                </span>
              </span>
            </span>
            <span {...stylex.props(styles.s7)}>
              <span
                {...stylex.props(dashboardStyles.activityAmount, item.positive ? dashboardStyles.positive : dashboardStyles.negative)}
              >
                {formatMoney(item.amount, data.currentUser.currency)}
              </span>
              <span {...stylex.props(styles.s22)}>
                {formatActivityDate(item.date)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function DashboardPage({
  data,
  onAddExpense,
  onCreateGroup,
  onOpenExpense,
  onOpenGroup,
  onOpenPerson,
  onSettle,
}: DashboardPageProps) {
  const owed = data.balances
    .filter((balance) => balance.amountCents > 0)
    .reduce((sum, balance) => sum + balance.amountCents, 0);
  const owing = data.balances
    .filter((balance) => balance.amountCents < 0)
    .reduce((sum, balance) => sum + Math.abs(balance.amountCents), 0);
  const net = owed - owing;
  const balances = data.balances.slice(0, 4);
  const month = new Intl.DateTimeFormat("en", { month: "long" }).format(
    new Date(),
  );

  return (
    <div {...stylex.props(styles.s23)}>
      <header {...stylex.props(styles.s24)}>
        <div>
          <p {...stylex.props(styles.s25)}>
            {month} overview
          </p>
          <h1 {...stylex.props(styles.s26)}>
            {greeting()}, {data.currentUser.name}
          </h1>
          <p {...stylex.props(styles.s27)}>
            Your shared money, kept simple and up to date.
          </p>
        </div>
        <div {...stylex.props(styles.s28)}>
          <button
            {...stylex.props(styles.s29)}
            onClick={onCreateGroup}
            type="button"
          >
            <Icon {...stylex.props(styles.s30)} name="groups" />
            New group
          </button>
          <button
            {...stylex.props(styles.s31)}
            onClick={() => onAddExpense()}
            type="button"
          >
            <Icon {...stylex.props(styles.s30)} name="plus" />
            Add expense
          </button>
        </div>
      </header>

      <section {...stylex.props(styles.s32)}>
        <article {...stylex.props(styles.s33)}>
          <p {...stylex.props(styles.s34)}>
            Your net balance
          </p>
          <p {...stylex.props(styles.s35)}>
            {formatMoney(Math.abs(net), data.currentUser.currency)}
          </p>
          <p {...stylex.props(styles.s36)}>
            <span {...stylex.props(styles.s37)}>
              <Icon
                {...stylex.props(styles.s38)}
                name={net >= 0 ? "arrow-right" : "arrow-left"}
              />
            </span>
            {net === 0
              ? "Everyone is square"
              : net > 0
                ? "You are ahead across your groups"
                : "You have a few balances to settle"}
          </p>
        </article>
        <SummaryCard
          amount={owed}
          caption={formatMoney(owed, data.currentUser.currency)}
          label="You are owed"
        />
        <SummaryCard
          amount={-owing}
          caption={formatMoney(owing, data.currentUser.currency)}
          label="You owe"
          tone="orange"
        />
      </section>

      <div {...stylex.props(styles.s39)}>
        <div {...stylex.props(styles.s40)}>
          <NetBalanceChart data={data} />
          <RecentActivity data={data} onOpenExpense={onOpenExpense} onOpenPerson={onOpenPerson} />
          <section {...stylex.props(styles.s3)}>
            <div {...stylex.props(styles.s4)}>
              <div>
                <h2 {...stylex.props(styles.s5)}>
                  Your groups
                </h2>
                <p {...stylex.props(styles.s6)}>
                  A home for the expenses that belong together
                </p>
              </div>
              <button
                {...stylex.props(styles.s41)}
                onClick={onCreateGroup}
                type="button"
              >
                Create one
              </button>
            </div>
            <div {...stylex.props(styles.s42)}>
              {data.groups.map((group, index) => (
                <button
                  {...stylex.props(styles.s43)}
                  key={group.id}
                  onClick={() => onOpenGroup(group.id)}
                  type="button"
                >
                  <div {...stylex.props(styles.s44)}>
                    <span
                      {...stylex.props(dashboardStyles.groupIcon, index % 2 === 0 ? dashboardStyles.groupIconGreen : dashboardStyles.groupIconTan)}
                    >
                      <Icon {...stylex.props(styles.s45)} name="groups" />
                    </span>
                    <Icon
                      {...stylex.props(styles.s46)}
                      name="arrow-right"
                    />
                  </div>
                  <h3 {...stylex.props(styles.s47)}>
                    {group.name}
                  </h3>
                  <p {...stylex.props(styles.s48)}>
                    {group.description ||
                      `${group.memberIds.length} people sharing expenses`}
                  </p>
                  <div {...stylex.props(styles.s49)}>
                    <span {...stylex.props(styles.s50)}>
                      {group.memberIds.length} people
                    </span>
                    <span {...stylex.props(styles.s51)}>
                      {formatMoney(
                        groupSpent(data, group.id),
                        data.currentUser.currency,
                      )}{" "}
                      spent
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>
        <aside {...stylex.props(styles.s3)}>
          <div {...stylex.props(styles.s4)}>
            <div>
              <h2 {...stylex.props(styles.s5)}>
                Settle up
              </h2>
              <p {...stylex.props(styles.s6)}>
                Record money that has changed hands
              </p>
            </div>
            <span {...stylex.props(styles.s14)}>
              <Icon {...stylex.props(styles.s15)} name="settle" />
            </span>
          </div>
          {balances.length > 0 ? (
            <div {...stylex.props(styles.s52)}>
              {balances.map((balance) => {
                const person = findPerson(data, balance.personId);
                if (!person) return null;
                const received = balance.amountCents > 0;
                return (
                  <div
                    {...stylex.props(styles.s53)}
                    key={balance.personId}
                  >
                    <button
                      {...stylex.props(styles.s54)}
                      onClick={() => onOpenPerson(balance.personId)}
                      type="button"
                    >
                      <Avatar person={person} size={36} />
                      <div {...stylex.props(styles.s55)}>
                        <p {...stylex.props(styles.s56)}>
                          {person.name}
                        </p>
                        <p
                          {...stylex.props(dashboardStyles.settlementLabel, received ? dashboardStyles.receivedLabel : dashboardStyles.owedLabel)}
                        >
                          {received ? "owes you" : "you owe"}
                        </p>
                      </div>
                      <p
                        {...stylex.props(dashboardStyles.settlementAmount, received ? dashboardStyles.receivedAmount : dashboardStyles.owedAmount)}
                      >
                        {formatMoney(
                          Math.abs(balance.amountCents),
                          balance.currency,
                        )}
                      </p>
                    </button>
                    <button
                      {...stylex.props(styles.s57)}
                      onClick={() => onSettle(balance)}
                      type="button"
                    >
                      {received ? "Record payment" : "Record payment sent"}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div {...stylex.props(styles.s58)}>
              <span {...stylex.props(styles.s59)}>
                <Icon {...stylex.props(styles.s60)} name="check" />
              </span>
              <p {...stylex.props(styles.s61)}>All settled up</p>
              <p {...stylex.props(styles.s6)}>
                New expenses will appear here when they need attention.
              </p>
            </div>
          )}
          <button
            {...stylex.props(styles.s62)}
            onClick={() => onAddExpense()}
            type="button"
          >
            <Icon {...stylex.props(styles.s30)} name="receipt" />
            Add an expense instead
          </button>
        </aside>
      </div>
    </div>
  );
}
